import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { translations } from "./translations";
import { DEFAULT_LANGUAGE, getCurrentLanguage, getLocalizedPath, isSupportedLanguage } from "@/utils/language";

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANGUAGE;
    return getCurrentLanguage(window.location.pathname);
  });
  const [langSwitchPaths, setLangSwitchPaths] = useState(null);

  const setLanguage = useCallback((next) => {
    const safe = isSupportedLanguage(next) ? next : DEFAULT_LANGUAGE;
    setLangState(safe);
  }, []);

  const toggle = useCallback(() => {
    setLangState((prev) => (prev === "en" ? DEFAULT_LANGUAGE : "en"));
  }, []);

  const localPath = useCallback((path) => {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    return getLocalizedPath(path, lang);
  }, [lang]);

  const getLanguageSwitchPath = useCallback((targetLang, pathname) => {
    const safe = isSupportedLanguage(targetLang) ? targetLang : DEFAULT_LANGUAGE;
    if (langSwitchPaths?.[safe]) return langSwitchPaths[safe];
    return getLocalizedPath(pathname, safe);
  }, [langSwitchPaths]);

  const t = useMemo(() => translations[lang] || translations[DEFAULT_LANGUAGE], [lang]);

  const value = useMemo(
    () => ({ lang, t, toggle, setLanguage, localPath, setLangSwitchPaths, getLanguageSwitchPath }),
    [lang, t, toggle, setLanguage, localPath, getLanguageSwitchPath],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used within LanguageProvider");
  return ctx;
};
